import { Turbo } from "@hotwired/turbo-rails"

const STATUS_SELECTOR = "[data-batch-ingest-live-status]"
const POLL_INTERVAL = 5000
const formStates = new WeakMap()
let workspace = null
let lifecycleBound = false

function submitButtons(form) {
  return Array.from(form.elements).filter((element) => element.type === "submit")
}

function rememberButtons(form, state) {
  submitButtons(form).forEach((button) => {
    if (!state.buttons.has(button)) {
      state.buttons.set(button, { disabled: button.disabled, html: button.innerHTML })
    }
  })
}

function lockButtons(form, state) {
  rememberButtons(form, state)
  submitButtons(form).forEach((button) => { button.disabled = true })
  if (state.submitter?.tagName === "BUTTON") {
    state.submitter.dataset.batchIngestActiveSubmit = "true"
    state.submitter.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>'
  }
}

function finishSubmission(form) {
  const state = formStates.get(form)
  if (!state) return

  state.buttons.forEach((original, button) => {
    button.disabled = original.disabled
    if (button.dataset.batchIngestActiveSubmit === "true") button.innerHTML = original.html
    delete button.dataset.batchIngestActiveSubmit
  })
  state.buttons.clear()
  state.submitting = false
  state.started = false
  state.submitter = null
  delete form.dataset.batchIngestSubmitting
  const status = form.querySelector("[data-batch-upload-submit-status]")
  if (status) {
    status.textContent = ""
    status.classList.remove("text-info", "text-success", "text-danger")
    status.classList.add("text-muted")
  }
}

function bindForm(form) {
  if (formStates.has(form)) {
    const state = formStates.get(form)
    if (state.started) lockButtons(form, state)
    return
  }

  const state = { submitting: false, started: false, submitter: null, buttons: new Map() }
  formStates.set(form, state)
  form.dataset.batchIngestSubmitBound = "true"
  form.addEventListener("submit", (event) => {
    if (state.submitting) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    state.submitter = event.submitter || submitButtons(form)[0]
    state.buttons.clear()
    rememberButtons(form, state)
  })
  form.addEventListener("turbo:submit-start", (event) => {
    state.submitting = true
    state.started = true
    state.submitter = event.detail?.formSubmission?.submitter || state.submitter
    form.dataset.batchIngestSubmitting = "true"
    const status = form.querySelector("[data-batch-upload-submit-status]")
    if (status) {
      const retry = state.submitter?.hasAttribute("formnovalidate")
      const count = form.querySelector('input[type="file"]')?.files?.length || 0
      status.textContent = retry ? "Restarting failed ingest jobs..." : `Uploading ${count || "selected"} PDF${count === 1 ? "" : "s"}...`
      status.classList.remove("text-muted", "text-success", "text-danger")
      status.classList.add("text-info")
    }
    lockButtons(form, state)
  })
  form.addEventListener("turbo:submit-end", () => {
    finishSubmission(form)
    if (workspace?.root.isConnected) refreshStatus(workspace, { force: true })
  })
}

function versionOf(element) {
  const value = element?.getAttribute?.("data-batch-status-version")
  if (value === null || value === undefined || value === "") return null
  const version = Number(value)
  return Number.isFinite(version) ? version : null
}

function incomingVersion(stream, targetId) {
  const streamVersion = versionOf(stream)
  if (streamVersion !== null) return streamVersion
  const content = stream.templateContent
  const incoming = Array.from(content?.querySelectorAll?.("[data-batch-status-version]") || [])
    .find((element) => element.id === targetId)
  return versionOf(incoming)
}

function batchStreamAllowed(stream) {
  const targetId = stream.getAttribute("target") || ""
  if (!targetId.startsWith("batch_ingest_")) return true
  if (stream.getAttribute("action") === "remove") return true

  const incoming = incomingVersion(stream, targetId)
  // Retired unversioned broadcasts must not restore an earlier batch's counts.
  if (incoming === null) return false
  const target = document.getElementById(targetId)
  let current = versionOf(target) ?? 0
  if (["batch_ingest_case_rows", "batch_ingest_retry_actions"].includes(targetId)) {
    current = Math.max(current, versionOf(document.getElementById("batch_ingest_live_summary")) ?? 0)
  }
  if (targetId === "batch_ingest_case_rows") {
    target?.querySelectorAll("[data-batch-status-version]").forEach((row) => {
      current = Math.max(current, versionOf(row) ?? 0)
    })
  }
  if (targetId.startsWith("batch_ingest_case_row_")) {
    const incomingRow = Array.from(stream.templateContent?.children || []).find((element) => element.id === targetId)
    const currentRun = target?.dataset.batchRunId
    const incomingRun = incomingRow?.dataset.batchRunId
    // A full snapshot introduces a new run; a late old-run row cannot replace it.
    if (currentRun && incomingRun && currentRun !== incomingRun) return false
  }
  return incoming >= current
}

function applyProgressWidths(root) {
  root.querySelectorAll("[data-progress-width]").forEach((element) => {
    const value = Number.parseFloat(element.dataset.progressWidth)
    const percent = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0
    if (element.firstElementChild) element.firstElementChild.style.width = `${percent}%`
  })
}

function activeCount(root) {
  const text = root.querySelector("#batch_ingest_live_summary_active")?.textContent || ""
  return Number.parseInt(text.match(/\d+/)?.[0] || "0", 10)
}

function stopWorkspace() {
  if (!workspace) return
  window.clearTimeout(workspace.timer)
  workspace.request?.abort()
  workspace.statusObserver.disconnect()
  workspace.connectionObserver.disconnect()
  workspace = null
}

function schedulePoll(state) {
  if (workspace !== state || !state.root.isConnected) {
    if (workspace === state) stopWorkspace()
    return
  }
  if (activeCount(state.root) === 0 || document.visibilityState === "hidden") {
    window.clearTimeout(state.timer)
    state.timer = null
    return
  }
  if (state.timer !== null || state.request) return
  state.timer = window.setTimeout(() => {
    state.timer = null
    refreshStatus(state)
  }, POLL_INTERVAL)
}

async function refreshStatus(state, { force = false } = {}) {
  if (workspace !== state || !state.root.isConnected) return
  if (state.request) {
    if (force) state.refreshPending = true
    return
  }
  if (!force && (activeCount(state.root) === 0 || document.visibilityState === "hidden")) return
  const url = state.root.dataset.batchIngestStatusUrl
  if (!url) return

  window.clearTimeout(state.timer)
  state.timer = null
  const request = new AbortController()
  state.request = request
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "text/vnd.turbo-stream.html" },
      credentials: "same-origin",
      signal: request.signal
    })
    if (!response.ok || !response.headers.get("Content-Type")?.includes("text/vnd.turbo-stream.html")) return
    const html = await response.text()
    if (workspace === state && state.root.isConnected && !request.signal.aborted) Turbo.renderStreamMessage(html)
  } catch (_error) {
    // Keep the last authoritative snapshot when a read-only refresh cannot connect.
  } finally {
    if (state.request === request) state.request = null
    if (workspace === state) {
      if (state.refreshPending) {
        state.refreshPending = false
        refreshStatus(state, { force: true })
      } else schedulePoll(state)
    }
  }
}

function startWorkspace(root) {
  const state = { root, timer: null, request: null, refreshPending: false, statusObserver: null, connectionObserver: null, connectedSources: new WeakSet() }
  workspace = state
  const sourceRoot = root.closest("turbo-frame") || document
  sourceRoot.querySelectorAll("turbo-cable-stream-source[connected]").forEach((source) => state.connectedSources.add(source))
  state.statusObserver = new MutationObserver(() => {
    if (!root.isConnected) return stopWorkspace()
    applyProgressWidths(root)
    schedulePoll(state)
  })
  state.statusObserver.observe(root, { childList: true, subtree: true, characterData: true })
  state.connectionObserver = new MutationObserver((mutations) => {
    if (!root.isConnected) return stopWorkspace()
    const frame = root.closest("turbo-frame")
    let reconnected = false
    mutations.forEach((mutation) => {
      const source = mutation.target
      if (mutation.type !== "attributes" || !source.matches?.("turbo-cable-stream-source[connected]")) return
      if (frame && !frame.contains(source)) return
      if (state.connectedSources.has(source)) reconnected = true
      state.connectedSources.add(source)
    })
    if (reconnected) refreshStatus(state, { force: true })
  })
  state.connectionObserver.observe(document.documentElement, {
    childList: true, subtree: true, attributes: true, attributeFilter: ["connected"]
  })
  applyProgressWidths(root)
  if (activeCount(root) > 0) refreshStatus(state)
}

function bindLifecycle() {
  if (lifecycleBound) return
  lifecycleBound = true
  document.addEventListener("turbo:before-stream-render", (event) => {
    const render = event.detail?.render
    const targetId = event.target?.getAttribute?.("target") || ""
    if (typeof render !== "function" || !targetId.startsWith("batch_ingest_")) return
    event.detail.render = async (stream) => {
      if (!batchStreamAllowed(stream)) return
      await render(stream)
      const target = document.getElementById(stream.getAttribute("target"))
      if (target && versionOf(stream) !== null) target.dataset.batchStatusVersion = String(versionOf(stream))
      if (workspace?.root.isConnected) {
        applyProgressWidths(workspace.root)
        schedulePoll(workspace)
      }
    }
  })
  const refreshCurrentWorkspace = () => {
    if (workspace?.root.isConnected) refreshStatus(workspace, { force: true })
  }
  window.addEventListener("online", refreshCurrentWorkspace)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      if (workspace) {
        window.clearTimeout(workspace.timer)
        workspace.timer = null
      }
    } else refreshCurrentWorkspace()
  })
  document.addEventListener("turbo:before-cache", () => {
    document.querySelectorAll("#batch-ingest-form").forEach(finishSubmission)
    stopWorkspace()
  })
  document.addEventListener("turbo:before-visit", stopWorkspace)
  document.addEventListener("turbo:before-frame-render", (event) => {
    if (workspace && event.target.contains(workspace.root)) stopWorkspace()
  })
}

export function initializeBatchIngestForms(root = document) {
  bindLifecycle()
  const forms = new Set(root.querySelectorAll?.("#batch-ingest-form") || [])
  if (root.matches?.("#batch-ingest-form")) forms.add(root)
  const pageForm = document.getElementById("batch-ingest-form")
  if (pageForm) forms.add(pageForm)
  forms.forEach(bindForm)

  const statusRoot = document.querySelector(STATUS_SELECTOR)
  if (workspace?.root !== statusRoot) stopWorkspace()
  if (statusRoot && !workspace) startWorkspace(statusRoot)
}
